import { Box, Divider, Grid, Input, Typography } from "@mui/joy";
import { Link, Link2 } from "lucide-react";
import { Button, IconButton, Alert } from "../../../../../../shared";
import { useEventHandler } from "../../hooks";

export function Basic({ details }: { details: any | null }) {
    const {
        slug,
        setSlug,
        projectType,
        setProjectType,
        status,
        setStatus,
        secondarySalesmanName,
        setSecondarySalesmanName,
        eagleViewOrderId,
        setEagleViewOrderId,
        saving,
        error,
        success,
        hasChanges,
        handleUpdate,
    } = useEventHandler(details);

    return (
        <Box sx={{ flex: 1 }}>
            <Box mb={1}>
                <Typography level="title-sm" component={"div"}>
                    Basic Information
                    <Typography level="body-xs" sx={{ opacity: 0.8, ml: 1 }} component={"span"}>
                        {details?.Id}
                    </Typography>
                </Typography>
                <Typography level="body-sm">You can edit the basic information of the project seamlessly.</Typography>
            </Box>

            {error && <Alert errorMessage={error} />}
            {success && <Alert successMessage={success} />}

            <Divider sx={{ mb: 3, mt: 2 }} />
            <Box display={"flex"} flexDirection={"column"} gap={2}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ flexBasis: 300 }}>
                        <Typography level="title-sm">EagleView Order ID</Typography>
                        <Typography level="body-sm">Project Id / EagleView order Id.</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Input
                            disabled={!!details?.EagleViewOrderId}
                            value={eagleViewOrderId}
                            onChange={(e) => setEagleViewOrderId(e.target.value)}
                            size="sm"
                            variant="outlined"
                        />
                    </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ flexBasis: 300 }}>
                        <Typography level="title-sm">Lead Source</Typography>
                        <Typography level="body-sm">Primary source of this lead.</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Input
                            value={details?.Lead?.LeadSource?.Name ?? "Not specified"}
                            disabled
                            size="sm"
                            variant="outlined"
                        />
                    </Box>
                </Box>
                {details?.Lead?.Name && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box sx={{ flexBasis: 300 }}>
                            <Typography level="title-sm">Secondary Lead</Typography>
                            <Typography level="body-sm">Person or company who brought this lead.</Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Input value={details.Lead.Name} disabled size="sm" variant="outlined" />
                        </Box>
                    </Box>
                )}

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ flexBasis: 300 }}>
                        <Typography level="title-sm">Status</Typography>
                        <Typography level="body-sm">Current status of the project.</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Input
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            size="sm"
                            variant="outlined"
                        />
                    </Box>
                </Box>
                <Divider sx={{ my: 1 }} />

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ flexBasis: 300 }}>
                        <Typography level="title-sm">Project Slug</Typography>
                        <Typography level="body-sm">This will be used to access the project.</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Input
                            startDecorator={<Link2 size={16} />}
                            value={slug}
                            onChange={(e) => setSlug(e.target.value)}
                            size="sm"
                            variant="outlined"
                        />
                    </Box>
                    <IconButton>
                        <Link size={16} />
                    </IconButton>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ flexBasis: 300 }}>
                        <Typography level="title-sm">Project Type</Typography>
                        <Typography level="body-sm">This will be used to access the project.</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Input
                            value={projectType}
                            onChange={(e) => setProjectType(e.target.value)}
                            size="sm"
                            variant="outlined"
                        />
                    </Box>
                </Box>
                <Divider sx={{ my: 1 }} />

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ flexBasis: 300 }}>
                        <Typography level="title-sm">Salesman Name</Typography>
                        <Typography level="body-sm">Primary sales representative.</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Input value={details?.SalesmanName ?? ""} disabled size="sm" variant="outlined" />
                    </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ flexBasis: 300 }}>
                        <Typography level="title-sm">Secondary Salesman Name</Typography>
                        <Typography level="body-sm">Additional sales representative.</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Input
                            value={secondarySalesmanName}
                            onChange={(e) => setSecondarySalesmanName(e.target.value)}
                            size="sm"
                            variant="outlined"
                            placeholder="Enter secondary salesman name"
                        />
                    </Box>
                </Box>

                <Box>
                    <Button
                        onClick={handleUpdate}
                        disabled={!hasChanges || saving}
                        loading={saving}
                        loadingPosition="end"
                    >
                        {saving ? "Updating" : "Update"}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
